const DEFAULT_MAX_ATTEMPTS = 20;

/**
 * Parent-keyed postUpsells only apply when history[-1] bought that parent.
 * Legacy Magellan groups without parentId stay applicable.
 */
export function postUpsellAppliesToPayload(data, payload) {
  if (data?.stepType !== "postUpsells") {
    return true;
  }
  const groups = data.productDetails?.postUpsells;
  if (!Array.isArray(groups) || groups.length === 0) {
    return true;
  }
  const last = payload?.history?.[payload.history.length - 1];
  const purchasedIds = new Set(
    (last?.choices || []).map((choice) => choice?.choiceId).filter(Boolean),
  );
  return groups.some(
    (group) => !group.parentId || purchasedIds.has(group.parentId),
  );
}

function cloneHistory(history) {
  return (history || []).map((item) => ({
    ...item,
    choices: Array.isArray(item.choices)
      ? item.choices.map((choice) => ({ ...choice }))
      : item.choices,
  }));
}

function skipInapplicablePostUpsell(payload, visitedSteps) {
  const history = cloneHistory(payload.history);
  const lastHistory = history[history.length - 1];
  if (!lastHistory) {
    return false;
  }
  if (lastHistory.action === "skip" && lastHistory.stepType === "postUpsells") {
    return false;
  }

  const current = Number(lastHistory.step);
  const skippedStep = Number.isFinite(current)
    ? current + 1
    : Number(payload.currentStep) + 1;
  if (!Number.isFinite(skippedStep) || visitedSteps.has(skippedStep)) {
    return false;
  }

  lastHistory.action = "skip";
  lastHistory.stepType = "postUpsells";
  lastHistory.step = skippedStep;
  visitedSteps.add(skippedStep);
  payload.history = history;
  payload.previousStepType = "postUpsells";
  payload.currentStep = skippedStep;
  return true;
}

/**
 * Orion /extrasell may return a step advance without stepType when the funnel
 * has no offer at the requested Dynamo step (e.g. cross-sell at step 2 while
 * currentStep is 0). Vanguard must chain calls until an offer or exhaustion.
 *
 * Orion signals exhaustion with funnelCompleted. Older Orion versions do not
 * send it, so maxAttempts stays as a safety net against an endless chain.
 */
export async function fetchExtrasellWithStepAdvance(
  postExtrasell,
  url,
  basePayload,
  maxAttempts = DEFAULT_MAX_ATTEMPTS,
) {
  const payload = { ...basePayload };
  let lastData = null;
  // Only ever move forward to a step never asked for, so a stalled or
  // oscillating Orion response ends the chain instead of consuming attempts.
  const visitedSteps = new Set([Number(payload.currentStep)]);

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const response = await postExtrasell(url, payload);
    const data = response?.data ?? null;
    if (!data) {
      break;
    }
    lastData = data;

    if (lastData.stepType && !postUpsellAppliesToPayload(lastData, payload)) {
      if (skipInapplicablePostUpsell(payload, visitedSteps)) {
        continue;
      }
      lastData = { ...lastData, stepType: null };
      break;
    }

    if (lastData.stepType) {
      return lastData;
    }

    if (lastData.funnelCompleted) {
      break;
    }

    if (lastData.step == null) {
      break;
    }

    const nextStep = Number(lastData.step);
    if (!Number.isFinite(nextStep) || visitedSteps.has(nextStep)) {
      break;
    }
    visitedSteps.add(nextStep);

    const history = [...(payload.history || [])];
    const lastHistory = history[history.length - 1];
    const stepTypeForHistory =
      payload.previousStepType ?? lastHistory?.stepType ?? null;

    if (stepTypeForHistory) {
      const chained = {
        step: nextStep,
        stepType: stepTypeForHistory,
        choices: lastHistory?.choices ?? [],
      };
      if (lastHistory?.action === "order") {
        // The empty slot was already consumed by the accepted extrasell.
        // A copied action=order would look ahead one more step and skip CS1.
        chained.action = "skip";
      } else if (lastHistory?.action) {
        chained.action = lastHistory.action;
      }
      history.push(chained);
      payload.history = history;
      payload.previousStepType = stepTypeForHistory;
    }

    payload.currentStep = nextStep;
  }

  if (lastData && !postUpsellAppliesToPayload(lastData, payload)) {
    return { ...lastData, stepType: null };
  }

  return lastData;
}

export function resolvePreviousStepTypeFromHistory(history, fallback = null) {
  if (!Array.isArray(history) || history.length === 0) {
    return fallback;
  }
  return history[history.length - 1]?.stepType ?? fallback;
}
